import api from "../api/axios";

const DEFAULT_MEDIA_BACKGROUND =
  "linear-gradient(135deg, rgba(73,74,152,.16), rgba(228,78,49,.12))";

export const LANDING_MEDIA_FALLBACK =
  "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='960' height='640' viewBox='0 0 960 640'%3E%3Crect width='960' height='640' fill='%23f1f3f7'/%3E%3Cg fill='none' stroke='%23949aab' stroke-width='24' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='286' y='170' width='388' height='300' rx='28'/%3E%3Ccircle cx='396' cy='276' r='36'/%3E%3Cpath d='m318 430 112-112 76 76 58-58 78 94'/%3E%3C/g%3E%3C/svg%3E";

export const TRANSPARENT_MEDIA_FALLBACK =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

function currentOrigin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

function currentProtocol() {
  return typeof window !== "undefined" ? window.location.protocol : "https:";
}

function isKnownMediaPageUrl(raw) {
  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    const path = url.pathname.toLowerCase();
    const googleHost = host === "google.com" || host.startsWith("google.") || host.includes(".google.");
    if (googleHost && (path === "/search" || path === "/imgres" || url.searchParams.has("imgurl"))) {
      return true;
    }
    if (host === "unsplash.com" && /^\/(?:[a-z]{2}\/)?photos?\//.test(path)) return true;
    if (host === "pexels.com" && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?photo\//.test(path)) return true;
    if (host === "pixabay.com" && /^\/(?:[a-z]{2}\/)?(?:photos|illustrations|vectors)\//.test(path)) return true;
    return false;
  } catch {
    return false;
  }
}

function safeAbsoluteMediaUrl(raw) {
  try {
    const url = new URL(raw);
    if (!["http:", "https:", "blob:"].includes(url.protocol)) return "";
    if (["http:", "https:"].includes(url.protocol) && isKnownMediaPageUrl(raw)) return "";
    return raw;
  } catch {
    return "";
  }
}

export function getMediaValue(input) {
  if (!input) return "";
  if (typeof input === "string" || typeof input === "number") return String(input);
  if (typeof input !== "object") return "";

  return String(
    input.publicUrl ||
      input.publicPath ||
      input.url ||
      input.path ||
      input.filename ||
      ""
  );
}

function escapeCssUrl(value) {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[\n\r\f]/g, "");
}

export function isSvgLandingMedia(input) {
  const raw = getMediaValue(input).trim();
  if (!raw) return false;
  if (/^data:image\/svg\+xml(?:[;,]|$)/i.test(raw)) return true;
  const clean = raw.split(/[?#]/, 1)[0].replace(/\\/g, "/").toLowerCase();
  return clean.endsWith(".svg");
}

export function buildMediaBackground(source, fallback = DEFAULT_MEDIA_BACKGROUND) {
  const url = String(source || "").trim();
  return url ? `url("${escapeCssUrl(url)}"), ${fallback}` : fallback;
}

export function handleMediaError(event, fallback = LANDING_MEDIA_FALLBACK) {
  const element = event?.currentTarget;
  if (!element) return;

  const nextSource = getMediaValue(fallback).trim() || LANDING_MEDIA_FALLBACK;
  if (element.dataset.wpMediaFallback === nextSource) return;

  element.dataset.wpMediaBroken = "true";
  element.dataset.wpMediaFallback = nextSource;
  element.removeAttribute("srcset");
  element.src = nextSource;
}

export function handleLandingMediaError(event, fallback = LANDING_MEDIA_FALLBACK) {
  const resolvedFallback = resolveLandingMedia(fallback, LANDING_MEDIA_FALLBACK);
  handleMediaError(event, resolvedFallback);
}

export function getApiOrigin() {
  const base = String(api?.defaults?.baseURL || "").trim();
  if (!base || base.startsWith("/")) return currentOrigin();
  return base.replace(/\/+$/, "").replace(/\/api\/?$/i, "");
}

/**
 * Resuelve cualquier asset público de landing de la misma forma que productos:
 * - /files/projects/<tenant>/landing/...      -> backend /files
 * - files/projects/<tenant>/landing/...       -> backend /files
 * - projects/<tenant>/landing/...             -> backend /files
 * - URL absoluta, data URL o blob URL
 * - assets estáticos propios del bundle
 */
export function resolveLandingMedia(input, fallback = "") {
  const raw = getMediaValue(input).trim();
  if (!raw || /[\u0000-\u001F\u007F]/.test(raw)) return fallback;

  if (/^data:image\//i.test(raw)) return raw;
  if (/^blob:/i.test(raw)) return safeAbsoluteMediaUrl(raw) || fallback;
  if (/^https?:\/\//i.test(raw)) return safeAbsoluteMediaUrl(raw) || fallback;
  if (raw.startsWith("//")) {
    return safeAbsoluteMediaUrl(`${currentProtocol()}${raw}`) || fallback;
  }

  const normalized = raw.replace(/\\/g, "/");
  if (normalized.startsWith("/static/") || normalized.startsWith("/assets/")) {
    return normalized;
  }

  const origin = getApiOrigin();
  if (normalized.startsWith("/files/")) return `${origin}${normalized}`;
  if (normalized.startsWith("files/")) return `${origin}/${normalized}`;
  if (normalized.startsWith("projects/")) return `${origin}/files/${normalized}`;
  if (normalized.startsWith("/projects/")) return `${origin}/files${normalized}`;

  if (normalized.startsWith("/")) return `${currentOrigin()}${normalized}`;

  // El backend público sólo admite URLs públicas o rutas absolutas. No
  // convertimos referencias relativas desconocidas en requests arbitrarios: si
  // una referencia quedó vieja o mal formada se usa el fallback seguro.
  return fallback;
}

export function getLandingBackgroundImage(input, fallback = DEFAULT_MEDIA_BACKGROUND) {
  return buildMediaBackground(resolveLandingMedia(input), fallback);
}

export function digitsOnly(value) {
  return String(value || "").replace(/\D+/g, "");
}

export function buildWhatsAppUrl(number, message = "") {
  const digits = digitsOnly(number);
  if (!digits) return "";
  const query = message ? `?text=${encodeURIComponent(message)}` : "";
  return `https://wa.me/${digits}${query}`;
}

export function fullBusinessAddress(business = {}) {
  return [business.address, business.city, business.province, business.country]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ");
}
